/**
 * AssessHub — /assess landing page (VER-53 shell, wired to real data in VER-119)
 *
 * Phase 3 entry point for practitioners and managers. Reads GET /api/assessments:
 * one card per programme, with pending pre/post counts derived from native
 * SurveyResponse rows. The MOCK_ASSESSMENTS stub this shell shipped with is gone.
 *
 * Remaining TODO: the route is currently reachable via the generic app shell. Once an
 * AssessView permit exists, gate the route with it (the endpoint itself already
 * requires ProgrammeView, so no data leaks in the meantime).
 */

import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, Grid, Tooltip, Typography } from "@mui/material";
import { AssessmentOutlined, GroupOutlined, PersonOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../hooks/useMobile";
import { VPill, VPillPositive } from "../../core/components/VPill";
import { HeadlineFigureWidget } from "../dashboard/Widgets/Headlines/HeadlineFigureWidget";
import Spinner from "../layout/Spinner";
import { Error as ErrorPanel } from "../../core/components/Error";
import { SimpleBlankSlate } from "../../core/components/SimpleBlankSlate";
import { isAssessPreviewEnabled, isSurveyBuilderOpenToAllOrgs } from "../../config/featureFlags";
import { useAuth } from "../../contexts/auth.context";
import { AssessmentCard, AssessmentStatus, AssessSummaryStats, listAssessments } from "../../client/api/assessment";

// Phase 3 (VER-51) surfaces still backed by mock data. These paths are registered in
// Routes.tsx ONLY when REACT_APP_ASSESS_PREVIEW is on (VER-112), so the links must be
// hidden under the same flag — otherwise production renders buttons that 404.
const PHASE3_PREVIEW_LINKS: { label: string; path: string }[] = [
    { label: "Group — live check-in (Model A)", path: "/assess/group-live/mock-1" },
    { label: "Participant join screen", path: "/assess/join-preview" },
    { label: "Assessment AI (5-prompt)", path: "/assess/ai/mock-1" },
    { label: "Participant impact tab", path: "/assess/impact-preview" },
];

// Shipped, real surfaces — always available, never behind the preview flag.
// `verbalOnly` links are internal Verbal tooling: the /survey/builder route carries a matching
// verbalOnly guard (BaseRoute), so for a partner organisation this button would only ever lead
// to /403. Hidden here for the same reason the preview links are hidden — no dead ends.
const LIVE_LINKS: { label: string; path: string; verbalOnly?: boolean }[] = [
    { label: "Survey builder (VER-108)", path: "/survey/builder", verbalOnly: true },
    { label: "Programme outcome reports (VER-109)", path: "/assess/reports" },
    { label: "Group — proxy read (Model B)", path: "/assess/group-proxy" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusLabel(status: AssessmentStatus): string {
    switch (status) {
        case "not-started":
            return "Not started";
        case "in-progress":
            return "In progress";
        case "complete":
            return "Complete";
    }
}

function StatusPill({ status }: { status: AssessmentStatus }): JSX.Element {
    if (status === "complete") {
        return <VPillPositive size="small" label={statusLabel(status)} />;
    }
    if (status === "in-progress") {
        return <VPill size="small" label={statusLabel(status)} color="primary" />;
    }
    return <VPill size="small" label={statusLabel(status)} />;
}

function formatDate(iso: string | null): string {
    if (!iso) {
        return "—";
    }
    return new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

// ---------------------------------------------------------------------------
// Summary stat widget — composes the shared HeadlineFigureWidget (outlined card,
// borders-over-shadows per DESIGN.md) instead of re-implementing the card inline.
// minHeight="auto" keeps the summary row compact; left-aligned to match the row.
// ---------------------------------------------------------------------------

type StatWidgetProps = {
    label: string;
    value: number;
};

const StatWidget = ({ label, value }: StatWidgetProps): JSX.Element => (
    <HeadlineFigureWidget minHeight="auto" centeredHeader={false}>
        <Typography variant="h3" color="primary">
            {value}
        </Typography>
        <Typography variant="body2" color="textSecondary">
            {label}
        </Typography>
    </HeadlineFigureWidget>
);

// ---------------------------------------------------------------------------
// Individual assessment card
// ---------------------------------------------------------------------------

const AssessmentCardItem = ({ card }: { card: AssessmentCard }): JSX.Element => {
    const navigate = useNavigate();

    const handleCardClick = () => {
        // Opens the assessment detail screen (AssessmentDetail, /assess/:id).
        navigate(`/assess/${card.id}`);
    };

    return (
        <Card
            sx={{
                cursor: "pointer",
                transition: "box-shadow 0.15s",
                "&:hover": {
                    boxShadow: (theme) => `0 2px 12px ${theme.palette.secondary.main}22`,
                },
            }}
            onClick={handleCardClick}
        >
            <CardContent>
                {/* Header row */}
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flex={1} mr={1}>
                        <Typography variant="h6" component="h2">
                            {card.title}
                        </Typography>
                    </Box>
                    <StatusPill status={card.status} />
                </Box>

                {/* Sub-line — real programme attributes. Programme has no description column,
                    so nothing is invented here; only what the record actually holds is shown. */}
                <Typography variant="body2" color="textSecondary" mb={2}>
                    {[
                        card.storyCollectionName,
                        card.ageRange,
                        `${card.participantCount} ${card.participantCount === 1 ? "participant" : "participants"}`,
                    ]
                        .filter(Boolean)
                        .join(" · ")}
                </Typography>

                {/* Meta row */}
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Tooltip title={card.mode === "1-2-1" ? "1-2-1 (Social worker)" : "Group (School)"}>
                        <Chip
                            size="small"
                            icon={card.mode === "1-2-1" ? <PersonOutlined /> : <GroupOutlined />}
                            label={card.mode === "1-2-1" ? "1-2-1" : "Group"}
                            variant="outlined"
                        />
                    </Tooltip>

                    {card.pendingPreSurvey > 0 && (
                        <Tooltip title="Participants awaiting pre-survey">
                            <Chip
                                size="small"
                                color="warning"
                                label={`${card.pendingPreSurvey} pre-survey pending`}
                                variant="outlined"
                            />
                        </Tooltip>
                    )}

                    {card.pendingPostSurvey > 0 && (
                        <Tooltip title="Participants awaiting post-survey">
                            <Chip
                                size="small"
                                color="info"
                                label={`${card.pendingPostSurvey} post-survey pending`}
                                variant="outlined"
                            />
                        </Tooltip>
                    )}

                    <Typography variant="body2" color="textSecondary" sx={{ ml: "auto" }}>
                        Updated {formatDate(card.lastUpdated)}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Main hub page
// ---------------------------------------------------------------------------

export const AssessHub = (): JSX.Element => {
    const { isMobile, isTablet } = useMobile();
    const navigate = useNavigate();
    const { user } = useAuth();
    const previewEnabled = isAssessPreviewEnabled();
    // The third arm is the TEMPORARY staging override — isSurveyBuilderOpenToAllOrgs(). All
    // three entry points (this hub, the nav entry, the route guard) have to agree, or the
    // hub keeps hiding the button the other two just opened.
    const liveLinks = LIVE_LINKS.filter((link) => !link.verbalOnly || user?.isVerbal || isSurveyBuilderOpenToAllOrgs());

    const [cards, setCards] = useState<AssessmentCard[]>([]);
    const [summary, setSummary] = useState<AssessSummaryStats | null>(null);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        listAssessments()
            .then((data) => {
                if (cancelled) return;
                setCards(data.items);
                setSummary(data.summary);
                setTruncated(data.truncated);
                setError(false);
            })
            .catch(() => {
                if (!cancelled) setError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const padding = !isTablet && isMobile ? 2 : 4;

    return (
        <Box p={padding} width={1}>
            {/* Mobile toolbar spacer — matches pattern used in ExploreScreen / Dashboard */}
            {!isTablet && isMobile && <Box pt={8} />}

            {/* Page header */}
            <Box mb={4} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                    <AssessmentOutlined color="primary" sx={{ fontSize: "2rem" }} />
                    <Typography variant="h2" color="textPrimary">
                        Assess
                    </Typography>
                </Box>
                <Button variant="contained" color="primary" onClick={() => navigate("/assess/new")}>
                    New assessment
                </Button>
            </Box>

            {loading ? (
                <Spinner />
            ) : error ? (
                <ErrorPanel description="Unable to load assessments" showImage={false} />
            ) : (
                <>
                    {/* Summary stats row. "Completed" counts programmes where every enrolled
                        participant has both administrations — the mock's "completed this term"
                        is gone because no term boundary exists in the schema to measure it. */}
                    <Box mb={4}>
                        <Typography variant="h5" color="textPrimary" mb={2}>
                            Overview
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <StatWidget label="Active assessments" value={summary?.totalAssessments ?? 0} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StatWidget label="Pending surveys" value={summary?.pendingSurveys ?? 0} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StatWidget label="Completed" value={summary?.completed ?? 0} />
                            </Grid>
                        </Grid>
                        {truncated && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Showing the {cards.length} most recent of {summary?.totalAssessments ?? 0} programmes.
                                Pending and completed totals cover only those shown.
                            </Alert>
                        )}
                    </Box>

                    {/* Assessment cards list */}
                    <Box>
                        <Typography variant="h5" color="textPrimary" mb={2}>
                            Your assessments
                        </Typography>

                        {cards.length === 0 ? (
                            <SimpleBlankSlate
                                header="No assessments yet"
                                content="Assessments appear here once you have a programme with participants. Start by creating a new assessment above."
                            />
                        ) : (
                            <Grid container spacing={2}>
                                {cards.map((card) => (
                                    <Grid item xs={12} key={card.id}>
                                        <AssessmentCardItem card={card} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                </>
            )}

            {/* Shipped Phase 3 tools — always available. */}
            <Box mt={4}>
                <Typography variant="h5" color="textPrimary" mb={2}>
                    Tools
                </Typography>
                <Grid container spacing={2}>
                    {liveLinks.map((link) => (
                        <Grid item xs={12} sm={6} md={4} key={link.path}>
                            <Button fullWidth variant="outlined" color="primary" onClick={() => navigate(link.path)}>
                                {link.label}
                            </Button>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Mock-data scaffolds. Hidden wherever the routes themselves are unregistered
                (VER-112), so these buttons can never point at a 404. */}
            {previewEnabled && (
                <Box mt={4}>
                    <Typography variant="h5" color="textPrimary" mb={2}>
                        Phase 3 surfaces (preview)
                    </Typography>
                    <Grid container spacing={2}>
                        {PHASE3_PREVIEW_LINKS.map((link) => (
                            <Grid item xs={12} sm={6} md={4} key={link.path}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => navigate(link.path)}
                                >
                                    {link.label}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};
